import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useToast } from '../../ui/Toast';
import { PageHead } from '../../shell/PageHead';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import { Modal } from '../../ui/Modal';

interface TemplateItem {
  id: string;
  company_id: string | null;
  nome: string;
  prompt: string;
  ordem: number;
  criado_em: string;
}

/** Tokens dinâmicos inseríveis no prompt (resolvidos no servidor — ADR-0023). */
const TOKENS = ['{keywords}', '{descricao}', '{dimensao}', '{tipo}', '{size}', '{quantidade}'];

const label: CSSProperties = { fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 4, display: 'block' };
const input: CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  fontSize: 13,
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-sm)',
  background: 'var(--bg-0)',
  color: 'var(--text-0)',
  outline: 'none'
};

export function Templates() {
  const { apiFetch, user } = useAuth();
  const toast = useToast();
  const isSuper = user?.papel === 'super_admin';

  const [items, setItems] = useState<TemplateItem[]>([]);
  const [system, setSystem] = useState<TemplateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<TemplateItem | null>(null);
  const [nome, setNome] = useState('');
  const [prompt, setPrompt] = useState('');
  const [ordem, setOrdem] = useState(100);
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  const promptRef = useRef<HTMLTextAreaElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await apiFetch<{ items: TemplateItem[] }>('/v1/admin/templates/');
    if (!isSuper) {
      const sys = await apiFetch<{ items: TemplateItem[] }>('/v1/admin/templates/system');
      if (sys.ok && sys.data) setSystem(sys.data.items);
    }
    setLoading(false);
    if (!r.ok || !r.data) {
      setError(r.error?.message ?? 'Falha ao carregar');
      return;
    }
    setItems(r.data.items);
  }, [apiFetch, isSuper]);

  useEffect(() => {
    load();
  }, [load]);

  function openNew() {
    setEditing(null);
    setNome('');
    setPrompt('');
    setOrdem(100);
    setFormErr(null);
    setOpen(true);
  }
  function openEdit(t: TemplateItem) {
    setEditing(t);
    setNome(t.nome);
    setPrompt(t.prompt);
    setOrdem(t.ordem);
    setFormErr(null);
    setOpen(true);
  }

  function insertToken(tok: string) {
    const el = promptRef.current;
    if (!el) {
      setPrompt((p) => p + tok);
      return;
    }
    const start = el.selectionStart ?? prompt.length;
    const end = el.selectionEnd ?? prompt.length;
    const next = prompt.slice(0, start) + tok + prompt.slice(end);
    setPrompt(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + tok.length;
      el.setSelectionRange(pos, pos);
    });
  }

  async function save() {
    if (!nome.trim() || !prompt.trim()) {
      setFormErr('Nome e prompt são obrigatórios');
      return;
    }
    setSaving(true);
    setFormErr(null);
    const body = { nome: nome.trim(), prompt: prompt.trim(), ordem };
    const r = editing
      ? await apiFetch(`/v1/admin/templates/${editing.id}`, { method: 'PATCH', body })
      : await apiFetch('/v1/admin/templates/', { method: 'POST', body });
    setSaving(false);
    if (!r.ok) {
      setFormErr(r.error?.message ?? 'Falha ao salvar');
      return;
    }
    toast(editing ? 'Template atualizado' : 'Template criado', 'success');
    setOpen(false);
    load();
  }

  async function remove(t: TemplateItem) {
    if (!window.confirm(`Excluir o template "${t.nome}"?`)) return;
    const r = await apiFetch(`/v1/admin/templates/${t.id}`, { method: 'DELETE' });
    if (!r.ok) {
      toast(r.error?.message ?? 'Falha ao excluir', 'danger');
      return;
    }
    toast('Template excluído', 'success');
    load();
  }

  const renderRow = (t: TemplateItem, idx: number, editable: boolean) => (
    <div
      key={t.id}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '12px 16px',
        borderTop: idx === 0 ? 'none' : '1px solid var(--border-soft)'
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="mono" style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-0)' }}>
            {t.nome}
          </span>
          {isSuper && idx === 0 && editable && (
            <Badge color="success" size="sm">padrão [00]</Badge>
          )}
        </div>
        <div
          style={{
            fontSize: 12,
            color: 'var(--text-2)',
            marginTop: 4,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: 60,
            overflow: 'hidden'
          }}
        >
          {t.prompt}
        </div>
      </div>
      {editable ? (
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <Button variant="ghost" size="sm" onClick={() => openEdit(t)}>Editar</Button>
          <Button variant="outline" size="sm" onClick={() => remove(t)}>Excluir</Button>
        </div>
      ) : (
        <Badge color="neutral" size="sm">sistema</Badge>
      )}
    </div>
  );

  return (
    <>
      <PageHead
        title="Templates"
        desc={
          isSuper
            ? 'Templates de prompt do sistema. O primeiro (menor ordem) é o padrão [00] usado quando a busca não informa promptAi.'
            : 'Seus templates de prompt para geração de imagem AI. Use no /v1/search com promptAi=<nome>.'
        }
        actions={
          <Button variant="brand" icon="plus" size="sm" onClick={openNew}>
            Novo template
          </Button>
        }
      />

      {error && (
        <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 'var(--r-sm)', padding: '10px 14px', fontSize: 13.5, marginBottom: 14 }}>
          {error}
        </div>
      )}

      {loading && <div className="skeleton" style={{ height: 80 }} />}

      {!loading && (
        <>
          <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', overflow: 'hidden', marginBottom: 20 }}>
            {items.length === 0 ? (
              <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text-2)', fontSize: 13.5 }}>
                Nenhum template ainda. Crie um com “Novo template”.
              </div>
            ) : (
              items.map((t, i) => renderRow(t, i, true))
            )}
          </div>

          {!isSuper && system.length > 0 && (
            <>
              <div className="eyebrow" style={{ marginBottom: 8 }}>Templates do sistema (herdáveis)</div>
              <p style={{ fontSize: 12.5, color: 'var(--text-3)', marginBottom: 8 }}>
                Disponíveis a todas as empresas. Use o nome em <code className="mono">promptAi=</code>. Se você
                criar um com o mesmo nome, o seu tem prioridade.
              </p>
              <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', overflow: 'hidden' }}>
                {system.map((t, i) => renderRow(t, i, false))}
              </div>
            </>
          )}
        </>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? `Editar · ${editing.nome}` : 'Novo template'}
        width={640}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button variant="brand" onClick={save} disabled={saving}>{saving ? 'Salvando…' : 'Salvar'}</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={label}>Nome</label>
              <input value={nome} onChange={(e) => setNome(e.target.value)} className="focusable" style={input} placeholder="ex.: produto-fundo-branco" />
            </div>
            <div style={{ width: 120 }}>
              <label style={label}>Ordem</label>
              <input type="number" value={ordem} onChange={(e) => setOrdem(Number(e.target.value))} className="focusable" style={input} />
            </div>
          </div>

          <div>
            <label style={label}>Prompt</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
              {TOKENS.map((tok) => (
                <button
                  key={tok}
                  type="button"
                  onClick={() => insertToken(tok)}
                  className="mono"
                  style={{ fontSize: 11.5, padding: '3px 8px', borderRadius: 999, border: '1px solid var(--border)', background: 'var(--bg-0)', color: 'var(--brand-strong)', cursor: 'pointer' }}
                >
                  {tok}
                </button>
              ))}
            </div>
            <textarea
              ref={promptRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={7}
              className="focusable"
              style={{ ...input, fontFamily: 'var(--font-mono)', fontSize: 12.5, lineHeight: 1.5, resize: 'vertical' }}
              placeholder="Foto profissional de {keywords}, {dimensao}, fundo neutro, alta resolução"
            />
            <p style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 4 }}>
              Os tokens são substituídos pelos valores da busca no momento da geração.
            </p>
          </div>

          {formErr && (
            <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 'var(--r-sm)', padding: '10px 12px', fontSize: 12.5 }}>
              {formErr}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
